// src/components/Center Admin/forms/GrowthChartEntry.tsx
import React, { useEffect, useState } from "react";
import ImageUploadBox from "../Reuse/ImageUploadBox";
import { ReactComponent as Upload } from "../../../assets/icons/upload.svg";
import { useS3Uploader } from "../../../hooks/useS3Uploader";
import "../../../center admin css/centerAdmin.css";
import api from "../../../api/axiosConfig";

interface GrowthChartFormProps {
  emrId: string;
}

type FormState = {
  heightCm: string;
  weightKg: string;
  fatherHeightCm: string;
  motherHeightCm: string;
  bmi: string;
};

const initialState: FormState = {
  heightCm: "",
  weightKg: "",
  fatherHeightCm: "",
  motherHeightCm: "",
  bmi: "",
};

const GrowthChartForm: React.FC<GrowthChartFormProps> = ({ emrId }) => {
  const [fields, setFields] = useState<FormState>(initialState);
  const [fileName, setFileName] = useState<string>("");
  const [uploadMsg, setUploadMsg] = useState<string>("");
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);

  // S3 uploader hook
  const { uploadImage, uploading, progress } = useS3Uploader(emrId);

  useEffect(() => {
    setFields(initialState);
    setUploadedUrls([]);
    setFileName("");
    setUploadMsg("");
  }, [emrId]);

  const num = (v: string) => (v === "" ? NaN : Number(v));

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (/^[0-9]*\.?[0-9]*$/.test(value)) {
      setFields((p) => ({ ...p, [name]: value }));
    }
  };

  // Auto calculate BMI
  useEffect(() => {
    const h = num(fields.heightCm);
    const w = num(fields.weightKg);
    if (!Number.isNaN(h) && !Number.isNaN(w) && h > 0) {
      const bmi = w / Math.pow(h / 100, 2);
      setFields((p) => ({ ...p, bmi: bmi ? bmi.toFixed(2) : "" }));
    } else {
      setFields((p) => ({ ...p, bmi: "" }));
    }
  }, [fields.heightCm, fields.weightKg]);

  // ----------------------------
  // 📌 HANDLE FILE UPLOAD (S3)
  // ----------------------------
  const onFilesSelected = async (files: File[]) => {
    const f = files?.[0];
    if (!f) return;

    setFileName(f.name);
    setUploadMsg("Uploading to S3…");

    try {
      const okType =
        f.type === "application/pdf" || f.type.startsWith("image/");

      if (!okType) throw new Error("Please upload a PDF or image.");
      if (f.size > 5 * 1024 * 1024) throw new Error("Max file size is 5 MB.");

      // Upload to S3 using moduleName = "growth-chart"
      const uploaded = await uploadImage(f, "growth-chart");

      if (uploaded?.key) {
        setUploadedUrls([uploaded.key]); // Only 1 allowed
        setUploadMsg("Uploaded successfully.");
      } else {
        throw new Error("Upload failed.");
      }
    } catch (err: any) {
      setUploadMsg(err?.message || "Upload failed.");
    }
  };

  useEffect(() => {
    const fetchAnthropometry = async () => {
      try {
        const response = await api.dataCollectionApi.get(
          `/api/forms/anthropometry/${emrId}`,
        );

        const data = response.data?.data;

        if (!data) return;

        setFields((prev) => ({
          ...prev,

          heightCm:
            data.heightCm !== undefined && data.heightCm !== null
              ? String(data.heightCm)
              : "",

          weightKg:
            data.weightKg !== undefined && data.weightKg !== null
              ? String(data.weightKg)
              : "",
        }));
      } catch (error) {
        console.error(
          "[GrowthChart] Failed to fetch anthropometry data:",
          error,
        );
      }
    };

    if (emrId) {
      fetchAnthropometry();
    }
  }, [emrId]);

  useEffect(() => {
    const fetchParentHeights = async () => {
      try {
        const response = await api.Questionnair.get(`/api/answers/${emrId}`);

        const answersData = response.data || [];

        // ✅ FIND FATHER HEIGHT
        const fatherHeightAnswer = answersData
          .flatMap((item: any) => item.answers || [])
          .find((answer: any) => answer.questionId === "CFP_F_4");

        // ✅ FIND MOTHER HEIGHT
        const motherHeightAnswer = answersData
          .flatMap((item: any) => item.answers || [])
          .find((answer: any) => answer.questionId === "CFP_M_4");

        setFields((prev) => ({
          ...prev,

          fatherHeightCm:
            fatherHeightAnswer?.value !== undefined &&
            fatherHeightAnswer?.value !== null
              ? String(fatherHeightAnswer.value)
              : "",

          motherHeightCm:
            motherHeightAnswer?.value !== undefined &&
            motherHeightAnswer?.value !== null
              ? String(motherHeightAnswer.value)
              : "",
        }));
      } catch (error) {
        console.error("[GrowthChart] Failed to fetch parent heights:", error);
      }
    };

    if (emrId) {
      fetchParentHeights();
    }
  }, [emrId]);

  // ----------------------------
  // 📌 SUBMIT FORM TO BACKEND
  // ----------------------------
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emrId) {
      alert("Missing EMR ID. Please select a valid patient.");
      return;
    }

    // ✅ VALIDATION
    if (
      !fields.heightCm ||
      !fields.weightKg ||
      !fields.fatherHeightCm ||
      !fields.motherHeightCm ||
      !fields.bmi
    ) {
      alert("Please fill all fields");
      return;
    }

    if (uploadedUrls.length === 0) {
      alert("Please upload Growth Chart PDF/Image");
      return;
    }

    const payload = {
      emrId,
      "Basic Measurements": {
        "Height (cm)": num(fields.heightCm),
        "Weight (kg)": num(fields.weightKg),
      },
      "Parents' Information": {
        "Father's Height (cm)": num(fields.fatherHeightCm),
        "Mother's Height (cm)": num(fields.motherHeightCm),
      },
      BMI: num(fields.bmi),

      // 📌 Save S3 URL(s)
      "Upload Growth Chart PDF/Image": uploadedUrls,
    };

    try {
      // ✅ GET SESSION ID FROM SELECTED PATIENT
      const storageKey = "selectedPatient_GROWTH_CHART_Q";
      const stored = sessionStorage.getItem(storageKey);

      const parsed = stored ? JSON.parse(stored) : null;
      const sessionId = parsed?.sessionId;
      console.log("SESSION USED IN SUBMIT:", sessionId);
      if (!sessionId) {
        alert("Session ID missing. Please reselect patient.");
        return;
      }

      const res = await api.dataCollectionApi.post(
        "/api/forms/growth-chart",
        payload,
        { headers: { "Content-Type": "application/json" } },
      );

      const data = res.data;
      if (res.status < 200 || res.status >= 300)
        throw new Error(data?.message || "Failed to submit");

      if (data?.success) {
        // ✅ CALL COMPLETE API
        await api.centerworkflowApi.post(
          `/sessions/${sessionId}/queues/GROWTH_CHART_Q/complete`,
          {
            payload: {
              remarks: "Growth chart submitted",
            },
          },
        );

        alert("Growth Chart submitted & moved to next step!");

        // Clear selected patient
        sessionStorage.removeItem("selectedPatient_GROWTH_CHART_Q");

        // Notify queue to reload
        window.dispatchEvent(new Event("patientUpdated"));

        setFields(initialState);
        setUploadedUrls([]);
        setFileName("");
      } else {
        alert("Submission failed");
      }
    } catch (err: any) {
      console.error("Error submitting Growth Chart:", err);
      alert(`Error submitting form: ${err?.message || "Unknown error"}`);
    }
  };

  return (
    <form className="gc-content-grid" onSubmit={submit}>
      {/* LEFT — FORM FIELDS */}
      <div className="gc-left-col">
        {/* Basic Measurements */}
        <div className="card gc-section">
          <div className="gc-section-title">Basic Measurements</div>
          <div className="gc-two-col">
            <div className="info-item">
              <label className="info-item__label">Height (cm)</label>
              <input
                className="info-item__input"
                name="heightCm"
                value={fields.heightCm}
                onChange={onChange}
              />
            </div>

            <div className="info-item">
              <label className="info-item__label">Weight (kg)</label>
              <input
                className="info-item__input"
                name="weightKg"
                value={fields.weightKg}
                onChange={onChange}
              />
            </div>
          </div>
        </div>

        {/* Parents Info */}
        <div className="card gc-section">
          <div className="gc-section-title">Parents' Information</div>
          <div className="gc-two-col">
            <div className="info-item">
              <label className="info-item__label">Father's Height (cm)</label>
              <input
                className="info-item__input"
                name="fatherHeightCm"
                value={fields.fatherHeightCm}
                onChange={onChange}
              />
            </div>

            <div className="info-item">
              <label className="info-item__label">Mother's Height (cm)</label>
              <input
                className="info-item__input"
                name="motherHeightCm"
                value={fields.motherHeightCm}
                onChange={onChange}
              />
            </div>
          </div>
        </div>

        {/* BMI */}
        <div className="card gc-section">
          <div className="gc-section-title">BMI</div>
          <div className="gc-one-col">
            <div className="info-item">
              <label className="info-item__label">BMI</label>
              <input
                className="info-item__input"
                name="bmi"
                readOnly
                value={fields.bmi}
              />
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — FILE UPLOADER */}
      <div className="gc-right-col">
        <div className="card gc-upload-card">
          <div className="gc-upload-title">Upload Growth Chart PDF/Image</div>

          <div className="gc-upload-box">
            <ImageUploadBox
              accept={[".pdf", "image/*"]}
              multiple={false}
              maxSizeMB={5}
              onFilesSelected={onFilesSelected}
            />
          </div>

          {fileName && (
            <div className="gc-upload-filename">Selected: {fileName}</div>
          )}

          {uploadMsg && <div className="gc-upload-status">{uploadMsg}</div>}

          {uploading && (
            <div className="gc-upload-status">Uploading… {progress}%</div>
          )}
        </div>
      </div>
      <button type="submit" className="gc-submit-btn">
        ✓ Submit
      </button>
    </form>
  );
};

export default GrowthChartForm;
